/**
 * Core Web Vitals Performance Monitor
 * Tracks LCP, INP, CLS, FCP, TTFB in production
 */

import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';
import { logger } from '@/lib/logger';
import { API_BASE_URL } from '@/lib/api';

interface Metric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id: string;
}

/**
 * Get performance rating based on thresholds
 */
function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds: Record<string, { good: number; poor: number }> = {
    LCP: { good: 2500, poor: 4000 },
    INP: { good: 200, poor: 500 }, // Interaction to Next Paint (replaces FID in v5)
    CLS: { good: 0.1, poor: 0.25 },
    FCP: { good: 1800, poor: 3000 },
    TTFB: { good: 800, poor: 1800 },
  };

  const threshold = thresholds[name];
  if (!threshold) return 'good';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Send metric to analytics endpoint
 */
function sendToAnalytics(metric: Metric) {
  // Only send in production
  if (import.meta.env.MODE !== 'production') {
    console.log('[Performance]', metric.name, metric.value, metric.rating);
    return;
  }

  // Use VITE_API_BASE_URL so requests go to the actual API (e.g. Railway) not the frontend origin
  const endpoint = `${API_BASE_URL.replace(/\/$/, '')}/analytics/web-vitals`;
  
  fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
      delta: metric.delta,
      url: window.location.href,
      timestamp: Date.now(),
      // Network info for per-connection analysis (Safari supports partially)
      effectiveType: (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType,
    }),
    keepalive: true, // Keep request alive even if page unloads
  }).catch(() => {
    // Silently fail if analytics endpoint is unavailable
  });
}

/**
 * Initialize Core Web Vitals monitoring
 */
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined') return;

  try {
    // Track LCP (Largest Contentful Paint)
    onLCP((metric) => {
      const rating = getRating('LCP', metric.value);
      sendToAnalytics({ ...metric, rating });
      
      // Alert if LCP is poor
      if (rating === 'poor' && import.meta.env.MODE === 'production') {
        logger.warn(`[Performance Alert] LCP is ${rating}: ${metric.value}ms`);
      }
    });

    // Track INP (Interaction to Next Paint) - replaces FID in web-vitals v5
    onINP((metric) => {
      const rating = getRating('INP', metric.value);
      sendToAnalytics({ ...metric, rating });
    });

    // Track CLS (Cumulative Layout Shift)
    onCLS((metric) => {
      const rating = getRating('CLS', metric.value);
      sendToAnalytics({ ...metric, rating });
      
      // Alert if CLS is poor
      if (rating === 'poor' && import.meta.env.MODE === 'production') {
        console.warn(`[Performance Alert] CLS is ${rating}: ${metric.value}`);
      }
    });

    // Track FCP (First Contentful Paint)
    onFCP((metric) => {
      const rating = getRating('FCP', metric.value);
      sendToAnalytics({ ...metric, rating });
    });

    // Track TTFB (Time to First Byte)
    onTTFB((metric) => {
      const rating = getRating('TTFB', metric.value);
      sendToAnalytics({ ...metric, rating });
    });
  } catch (error) {
    // Silently fail if web-vitals library not available
    logger.warn('[Performance] web-vitals library not available:', error);
  }
}

/**
 * Track custom performance metric
 */
export function trackCustomMetric(name: string, value: number, unit: string = 'ms') {
  if (typeof window === 'undefined') return;

  const metric: Metric = {
    name,
    value,
    rating: 'good', // Custom metrics don't have ratings
    id: `${name}-${Date.now()}`,
  };

  sendToAnalytics(metric);
}

